// pricing.jsx - PyK7VM Pricing & Plan Comparison
// Compiled to dist/pricing.js via build.mjs. Loaded by pricing.php.

const { useState, useEffect } = React;
const { Reveal, Aurora } = window;
const { Nav, Footer, DiscordCTA } = window;

// ─── FAQ ACCORDION ITEM (Multi-Expand Supported) ──────────────────────────────
function AccordionItem({ q, a }) {
  const [open, setOpen] = useState(false);
  const [btnHover, setBtnHover] = useState(false);

  return (
    <div
      style={{
        borderBottom: '1px solid var(--border)',
        transition: 'border-color .25s ease',
        borderColor: open ? 'rgba(59,130,246,0.35)' : 'var(--border)',
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '20px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          minHeight: 48,
        }}
      >
        <span
          style={{
            fontSize: 'clamp(15px, 2vw, 17px)',
            fontWeight: 500,
            color: open ? 'var(--text)' : btnHover ? 'var(--text)' : 'var(--text-dim)',
            letterSpacing: -0.3,
            lineHeight: 1.4,
            transition: 'color .2s ease',
          }}
        >
          {q}
        </span>
        <span
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: `1px solid ${open ? 'var(--brand)' : btnHover ? 'var(--border-strong)' : 'var(--border)'}`,
            background: open ? 'rgba(59,130,246,0.12)' : 'rgba(245,241,232,0.02)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: open ? 'var(--brand-soft)' : 'var(--text-muted)',
            fontSize: 18,
            transition: 'all .25s ease',
            transform: open ? 'rotate(45deg)' : 'none',
            marginTop: 1,
          }}
        >
          +
        </span>
      </button>
      <div
        style={{
          overflow: 'hidden',
          maxHeight: open ? 500 : 0,
          opacity: open ? 1 : 0,
          transition: 'max-height .35s cubic-bezier(.4,0,.2,1), opacity .25s ease',
        }}
      >
        <p
          style={{
            paddingBottom: 22,
            color: 'var(--text-dim)',
            fontSize: 'clamp(13.5px, 1.8vw, 15px)',
            lineHeight: 1.7,
            maxWidth: 780,
            margin: 0,
          }}
        >
          {a}
        </p>
      </div>
    </div>
  );
}

// ─── HERO SECTION ─────────────────────────────────────────────────────────────
function PricingHero() {
  const trustBadges = [
    {
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-soft)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      ),
      label: '100% Free During Beta',
    },
    {
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-soft)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      ),
      label: 'No Credit Card Required',
    },
    {
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-soft)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      ),
      label: 'Commercial Rights Included',
    },
    {
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--brand-soft)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
      label: 'Zero Telemetry & Ephemeral',
    },
  ];

  return (
    <section style={{ position: 'relative', padding: 'clamp(96px, 12vw, 150px) clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px)', textAlign: 'center', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.3, pointerEvents: 'none' }}>
        <Aurora palette={{ a: '#1e40af', b: '#3b82f6', c: '#08080d' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 2, maxWidth: 880, margin: '0 auto' }}>
        <Reveal>
          <h1
            style={{
              fontSize: 'clamp(34px, 5.5vw, 72px)',
              fontWeight: 500,
              letterSpacing: -2,
              lineHeight: 1.06,
              color: 'var(--text)',
              margin: '0 auto 16px',
              textWrap: 'balance',
            }}
          >
            Start free.{' '}
            <span style={{ fontWeight: 400, color: 'var(--brand-soft)' }}>
              Go deeper with Beta.
            </span>
          </h1>
        </Reveal>

        <Reveal delay={60}>
          <p
            style={{
              marginTop: 0,
              marginBottom: 32,
              color: 'var(--text-dim)',
              fontSize: 'clamp(14.5px, 2vw, 16.5px)',
              lineHeight: 1.65,
              maxWidth: 680,
              marginLeft: 'auto',
              marginRight: 'auto',
              fontFamily: 'var(--font-ui)',
            }}
          >
            Every account gets basic native protection for free. Request beta access to unlock the full VM engine - no payment needed while beta is open.
          </p>
        </Reveal>

        {/* 4 Trust Badges */}
        <Reveal delay={120}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 10,
              marginTop: 8,
            }}
          >
            {trustBadges.map((badge, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 14px',
                  borderRadius: 999,
                  background: 'rgba(245,241,232,0.02)',
                  border: '1px solid var(--border)',
                  fontSize: 12.5,
                  color: 'var(--text-dim)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {badge.icon}
                </span>
                <span>{badge.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── TIER FEATURE ROW ─────────────────────────────────────────────────────────
function FeatureRow({ label, included = true, subtext = null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
      <span
        style={{
          color: included ? 'var(--success)' : 'var(--text-muted)',
          fontWeight: 700,
          fontSize: 14,
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        {included ? '✓' : '–'}
      </span>
      <div>
        <span style={{ color: included ? 'var(--text)' : 'var(--text-muted)', fontSize: 13.5, lineHeight: 1.5 }}>
          {label}
        </span>
        {subtext && (
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
            {subtext}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PRICING TIER CARDS ───────────────────────────────────────────────────────
function PricingTiers() {
  const [hoveredCard, setHoveredCard] = useState(null);

  const cardStyle = (isFeatured, isHovered) => ({
    background: isFeatured
      ? 'linear-gradient(180deg, rgba(59,130,246,0.06) 0%, rgba(245,241,232,0.015) 100%)'
      : 'rgba(245,241,232,0.015)',
    border: isFeatured
      ? '1px solid rgba(59,130,246,0.45)'
      : `1px solid ${isHovered ? 'var(--border-strong)' : 'var(--border)'}`,
    borderRadius: 22,
    padding: 'clamp(24px, 4vw, 36px) clamp(18px, 3.5vw, 30px)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backdropFilter: 'blur(16px)',
    boxShadow: isFeatured
      ? '0 0 60px rgba(59,130,246,0.14), inset 0 0 20px rgba(59,130,246,0.04)'
      : isHovered
      ? '0 12px 36px rgba(0,0,0,0.35)'
      : 'none',
    transform: isHovered ? 'translateY(-4px)' : 'none',
    transition: 'all .3s cubic-bezier(.16,.77,.28,.99)',
    minWidth: 0,
  });

  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 90px) clamp(16px, 4vw, 32px) clamp(60px, 8vw, 100px)', position: 'relative' }}>
      <style>{`
        .pricing-grid { display: flex; gap: 24px; align-items: stretch; }
        @media (max-width: 960px) {
          .pricing-grid { flex-direction: column !important; align-items: center !important; }
          .pricing-grid > div { width: 100% !important; max-width: 520px !important; }
        }
        @media (min-width: 961px) {
          .pricing-grid { flex-direction: row !important; align-items: stretch !important; }
          .pricing-grid > div { width: auto !important; max-width: none !important; flex: 1 1 0% !important; }
        }
        html.light .pricing-tier-card {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
        }
        html.light .pricing-tier-card.is-featured {
          background: #ffffff !important;
          border-color: rgba(37, 99, 235, 0.45) !important;
          box-shadow: 0 8px 30px rgba(37, 99, 235, 0.12) !important;
        }
        html.light .enterprise-banner-box,
        html.light .compare-matrix-box {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
        }
      `}</style>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>

        {/* Urgency Banner */}
        <Reveal>
          <div
            style={{
              maxWidth: 1240,
              margin: '0 auto clamp(24px, 4vw, 36px)',
              padding: '12px 18px',
              borderRadius: 14,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              color: '#fca5a5',
              fontSize: 13.5,
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 8px #ef4444', flexShrink: 0 }} />
            <span>Beta access is open. Approved beta accounts get the full protection stack at no cost.</span>
          </div>
        </Reveal>

        <Reveal delay={60}>
          <div className="pricing-grid">

            {/* 1. FREE TIER */}
            <div
              className="pricing-tier-card"
              onMouseEnter={() => setHoveredCard('free')}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ ...cardStyle(false, hoveredCard === 'free'), flex: 1 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 19, fontWeight: 600, color: 'var(--text)', letterSpacing: -0.3 }}>Free</span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-code)',
                    background: 'rgba(245,241,232,0.04)',
                    padding: '2px 8px',
                    borderRadius: 999,
                    border: '1px solid var(--border)',
                  }}
                >
                  Permanent
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, margin: '10px 0 6px' }}>
                <span style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1.5, color: 'var(--text)' }}>$0</span>
                <span style={{ fontSize: 13.5, fontWeight: 400, color: 'var(--text-muted)' }}>/ forever</span>
              </div>

              <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, marginBottom: 24, minHeight: 40 }}>
                Compiled to a native .pyd binary with basic variable/class renaming and size compression.
              </p>

              <div style={{ flex: 1, marginBottom: 28 }}>
                <FeatureRow label="Compiled native .pyd binary" subtext="Windows & Linux, x64 + ARM64" />
                <FeatureRow label="AST symbol renaming & compression" />
                <FeatureRow label="Free profile (30 decoys)" />
                <FeatureRow label="5 builds per day (single .py)" subtext="Failed jobs never consume quota" />
                <FeatureRow label="Python 3.11, 3.12, 3.13, 3.13t" />
                <FeatureRow label="Active dump detection" included={false} />
                <FeatureRow label="REST API v1 access" included={false} subtext="Web UI submission only" />
                <FeatureRow label="Community Discord support" />
              </div>

              <a
                href="register.php"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '13px 20px',
                  borderRadius: 12,
                  border: '1px solid var(--border-strong)',
                  background: 'rgba(245,241,232,0.02)',
                  color: 'var(--text)',
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all .25s ease',
                  minHeight: 46,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)';
                  e.currentTarget.style.background = 'rgba(59,130,246,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-strong)';
                  e.currentTarget.style.background = 'rgba(245,241,232,0.02)';
                }}
              >
                Start Free Build
              </a>
            </div>

            {/* 2. BETA TIER (FEATURED) */}
            <div
              onMouseEnter={() => setHoveredCard('beta')}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ ...cardStyle(true, hoveredCard === 'beta'), flex: 1 }}
            >
              {/* Floating Pill Badge */}
              <div
                style={{
                  position: 'absolute',
                  top: -12,
                  right: 20,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: '#fff',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  padding: '4px 12px',
                  borderRadius: 999,
                  boxShadow: '0 4px 14px rgba(59,130,246,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                Beta - Free Now
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 19, fontWeight: 600, color: 'var(--text)', letterSpacing: -0.3 }}>Beta Tier</span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: 'var(--brand-soft)',
                    fontFamily: 'var(--font-code)',
                    background: 'rgba(59,130,246,0.1)',
                    padding: '2px 8px',
                    borderRadius: 999,
                    border: '1px solid rgba(59,130,246,0.3)',
                  }}
                >
                  Featured
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, margin: '10px 0 6px' }}>
                <span style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1.5, color: 'var(--text)' }}>Free</span>
                <span style={{ fontSize: 13.5, fontWeight: 400, color: 'var(--brand-soft)' }}>/ during public beta</span>
              </div>

              <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, marginBottom: 24, minHeight: 40 }}>
                Full virtualization engine with premium VM profiles and dump detection. Requires beta approval.
              </p>

              <div style={{ flex: 1, marginBottom: 28 }}>
                <FeatureRow label="Full VM Bytecode Protection" subtext="Eliminates standard CPython code objects" />
                <FeatureRow label="Polymorphic Opcode Dispatch" subtext="64 randomized opcodes per build" />
                <FeatureRow label="Anti-Debug & Anti-Tamper Traps" />
                <FeatureRow label="Dump detection & self-destruct" subtext="Zero-fill memory scrub on tamper" />
                <FeatureRow label="20 builds per day" subtext="Up to 10 MB source zip archive" />
                <FeatureRow label="Windows + Linux, x64 + ARM64" subtext="Native .pyd, .so &amp; .exe outputs" />
                <FeatureRow label="REST API v1 + CLI automation" />
                <FeatureRow label="Priority Discord support" />
              </div>

              <a
                href="register.php"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '13px 20px',
                  borderRadius: 12,
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(59,130,246,0.35)',
                  transition: 'all .25s ease',
                  minHeight: 46,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 6px 26px rgba(59,130,246,0.5)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(59,130,246,0.35)';
                  e.currentTarget.style.transform = 'none';
                }}
              >
                Request beta access
              </a>
            </div>

            {/* 3. PREMIUM TIER (SOON) */}
            <div
              onMouseEnter={() => setHoveredCard('premium')}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ ...cardStyle(false, hoveredCard === 'premium'), flex: 1 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 19, fontWeight: 600, color: 'var(--text)', letterSpacing: -0.3 }}>Premium</span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-code)',
                    background: 'rgba(245,241,232,0.04)',
                    padding: '2px 8px',
                    borderRadius: 999,
                    border: '1px solid var(--border)',
                  }}
                >
                  Post-Beta
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, margin: '10px 0 6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1.5, color: 'var(--text)' }}>$49</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>/ mo</span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: 'var(--brand-soft)',
                    background: 'rgba(59,130,246,0.1)',
                    border: '1px solid rgba(59,130,246,0.25)',
                    padding: '2px 6px',
                    borderRadius: 999,
                  }}
                >
                  40% Off Early
                </span>
              </div>

              <p style={{ color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.6, marginBottom: 24, minHeight: 40 }}>
                Everything in Beta, unwatermarked distribution modules, and dedicated priority support channels.
              </p>

              <div style={{ flex: 1, marginBottom: 28 }}>
                <FeatureRow label="Unlimited builds" subtext="No daily or monthly quota caps" />
                <FeatureRow label="500 MB max source zip" />
                <FeatureRow label="Windows + Linux, x64 + ARM64" subtext="Native .pyd, .so &amp; .exe outputs" />
                <FeatureRow label="Paranoid profile (10,000+ decoys)" />
                <FeatureRow label="Clean unwatermarked distribution" />
                <FeatureRow label="Custom C++ module namespaces" />
                <FeatureRow label="Audit logs & webhook feeds" />
                <FeatureRow label="Dedicated SLA & email support" />
              </div>

              <a
                href="https://discord.gg/hpx6XkqmKM"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '13px 20px',
                  borderRadius: 12,
                  border: '1px solid var(--border-strong)',
                  background: 'rgba(245,241,232,0.02)',
                  color: 'var(--text)',
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all .25s ease',
                  minHeight: 46,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.5)';
                  e.currentTarget.style.background = 'rgba(59,130,246,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-strong)';
                  e.currentTarget.style.background = 'rgba(245,241,232,0.02)';
                }}
              >
                Notify Me at Launch
              </a>
            </div>

          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── ENTERPRISE AIR-GAPPED BANNER ─────────────────────────────────────────────
function EnterpriseBanner() {
  const [btnHover, setBtnHover] = useState(false);

  const badges = [
    { title: 'Air-Gapped CLI & Binary', desc: 'No outbound network requests' },
    { title: 'Zero Telemetry Guarantee', desc: '100% local sovereign compilation' },
    { title: 'Custom Decoy Trees', desc: 'Tailored opcode instruction sets' },
    { title: 'Dedicated SLA & Escrow', desc: 'Direct senior engineer escalation' },
  ];

  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: '0 clamp(16px, 4vw, 32px) clamp(50px, 7vw, 80px)' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal>
          <div
            className="card panel enterprise-banner-box"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border-strong)',
              borderRadius: 22,
              padding: 'clamp(24px, 4vw, 44px) clamp(18px, 4vw, 40px)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15), inset 0 0 30px rgba(59,130,246,0.05)',
            }}
          >
            <style>{`
              .ent-split { display: flex; justify-content: space-between; align-items: center; gap: 36px; }
              .ent-badges { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
              @media (max-width: 900px) {
                .ent-split { flex-direction: column !important; gap: 24px !important; }
                .ent-badges { grid-template-columns: 1fr !important; }
              }
              html.light .enterprise-banner-box {
                background: #ffffff !important;
                border-color: rgba(37, 99, 235, 0.25) !important;
                box-shadow: 0 10px 40px -4px rgba(37, 99, 235, 0.08), 0 2px 12px rgba(15, 23, 42, 0.04) !important;
              }
              html.light .ent-badge-item {
                background: #f8fafc !important;
                border-color: rgba(15, 23, 42, 0.08) !important;
              }
            `}</style>
            <div className="ent-split">
              
              <div style={{ maxWidth: 660 }}>
                <h3
                  style={{
                    fontSize: 'clamp(22px, 3vw, 32px)',
                    fontWeight: 500,
                    letterSpacing: -1,
                    lineHeight: 1.15,
                    color: 'var(--text)',
                    marginBottom: 12,
                  }}
                >
                  Air-Gapped &amp; Self-Hosted Deployments
                </h3>
                <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(14px, 1.8vw, 15px)', lineHeight: 1.65, margin: '0 0 20px' }}>
                  On the product roadmap for defense, financial institutions, and sovereign compliance teams requiring standalone offline compiler binaries with zero external connectivity.
                </p>

                <div className="ent-badges">
                  {badges.map((b, i) => (
                    <div
                      key={i}
                      className="ent-badge-item"
                      style={{
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        padding: '10px 12px',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', letterSpacing: -0.2 }}>
                        {b.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {b.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ flexShrink: 0, textAlign: 'center', width: '100%', maxWidth: 300 }}>
                <a
                  href="mailto:sales@pyk7vm.com?subject=PyK7VM%20Enterprise%20Compiler%20Inquiry"
                  onMouseEnter={() => setBtnHover(true)}
                  onMouseLeave={() => setBtnHover(false)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--text)',
                    opacity: btnHover ? 0.92 : 1,
                    color: 'var(--bg)',
                    padding: '13px 26px',
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 14,
                    textDecoration: 'none',
                    letterSpacing: -0.2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                    transform: btnHover ? 'translateY(-2px)' : 'none',
                    transition: 'all .25s ease',
                    minHeight: 46,
                    width: '100%',
                  }}
                >
                  Contact Enterprise Sales
                </a>
              </div>

            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── DEEP CATEGORIZED COMPARISON TABLE ────────────────────────────────────────
const TABLE_CATEGORIES = [
  {
    category: 'Compilation & Quotas',
    rows: [
      { label: 'Daily Build Limit', sub: 'Calculated on 24h rolling reset', free: '5 / day', beta: '20 / day', premium: 'Unlimited' },
      { label: 'Max Source Archive Size', sub: 'Compressed .zip upload limit', free: '2 MB', beta: '10 MB', premium: '500 MB' },
      { label: 'Python Versions', sub: 'Target CPython runtime matrix', free: '3.11 / 3.12 / 3.13 / 3.13t', beta: '3.11 / 3.12 / 3.13 / 3.13t', premium: '3.11 / 3.12 / 3.13 / 3.13t + Previews' },
      { label: 'Target Platforms', sub: 'Compiled native extension format', free: 'Windows x64/ARM64 (.pyd)', beta: 'Windows + Linux, x64/ARM64', premium: 'Windows + Linux, x64/ARM64' },
      { label: 'Failed Job Exemption', sub: 'Syntax errors do not consume quota', free: true, beta: true, premium: true },
    ],
  },
  {
    category: 'Obfuscation & Virtualization Engine',
    rows: [
      { label: 'Polymorphic VM Bytecode', sub: 'Randomized opcodes generated per build', free: 'Basic (30 opcodes)', beta: 'Full dynamic (64 opcodes)', premium: 'Deep custom opcode sets' },
      { label: 'AST Mutation & Invariants', sub: 'Symbol rewriting & opaque predicates', free: true, beta: true, premium: true },
      { label: 'Decoy Routine Farm', sub: 'Dead-code control flow diverters', free: '30 decoys', beta: '1,000+ decoys', premium: 'Up to 10,000 decoys' },
      { label: 'Cryptographic Entropy Seed', sub: 'Derived from CPU timestamp & OS RNG', free: 'Static seed', beta: '256-bit entropy seed', premium: '256-bit + HSM custom seed' },
      { label: 'Custom C++ Module Namespaces', sub: 'Replace default entrypoints & symbols', free: false, beta: false, premium: true },
    ],
  },
  {
    category: 'Runtime Anti-Tamper & Defense',
    rows: [
      { label: 'Anti-Debugging Traps', sub: 'PEB integrity & hardware breakpoint traps', free: 'Basic PEB trap', beta: 'Multi-layer traps', premium: 'Kernel + hardware traps' },
      { label: 'Active Dump Detection', sub: 'Detects memory scrapers & injector hooks', free: false, beta: true, premium: true },
      { label: 'Zero-Fill Memory Scrubbing', sub: 'Decrypted constants erased in microseconds', free: false, beta: true, premium: true },
      { label: 'Silent Tamper State Divergence', sub: 'Silently corrupts internal execution flow', free: false, beta: true, premium: true },
      { label: 'Self-Destruct Killswitch', sub: 'Immediate process termination on tamper', free: false, beta: true, premium: true },
    ],
  },
  {
    category: 'Automation & Integrations',
    rows: [
      { label: 'REST API v1 Access', sub: 'Programmatic compilation submission', free: false, beta: true, premium: true },
      { label: 'CLI Tool Automation', sub: 'Drop-in terminal compiler command', free: true, beta: true, premium: true },
      { label: 'CI/CD Pipeline Integration', sub: 'GitHub Actions / GitLab CI triggers', free: 'Manual script', beta: 'CLI supported', premium: 'Dedicated action + bot token' },
      { label: 'Realtime Webhook Feeds', sub: 'Job completion & status callbacks', free: false, beta: true, premium: true },
    ],
  },
  {
    category: 'Commercial Rights & Support',
    rows: [
      { label: 'Commercial Software Rights', sub: '100% royalty-free distribution', free: true, beta: true, premium: true },
      { label: 'Binary Permanence', sub: 'Compiled binaries never expire or phone home', free: true, beta: true, premium: true },
      { label: 'Support Channels', sub: 'Direct engineering assistance', free: 'Community Discord', beta: 'Priority Discord', premium: 'Dedicated Slack + Email' },
      { label: 'Service Level Agreement (SLA)', sub: 'Guaranteed uptime & build processing', free: 'Best effort', beta: 'Best effort', premium: '99.9% Uptime Guarantee' },
    ],
  },
];

function CellVal({ val }) {
  if (val === true) {
    return (
      <span
        style={{
          color: 'var(--success)',
          fontWeight: 700,
          fontSize: 15,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'rgba(16,185,129,0.1)',
        }}
      >
        ✓
      </span>
    );
  }
  if (val === false) {
    return <span style={{ color: 'var(--text-muted)', fontSize: 15 }}>–</span>;
  }
  return <span style={{ color: 'var(--text-dim)', fontSize: 13, fontWeight: 500 }}>{val}</span>;
}

function CompareTable() {
  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal>
          <div style={{ marginBottom: 36 }}>
            <h2
              style={{
                fontSize: 'clamp(28px, 4.5vw, 48px)',
                fontWeight: 500,
                letterSpacing: -1.5,
                lineHeight: 1.08,
                color: 'var(--text)',
                margin: 0,
              }}
            >
              Compare plan capabilities
            </h2>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="table-responsive-wrapper table-sticky-col">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Feature</th>
                  <th style={{ width: '20%', textAlign: 'center' }}>Free</th>
                  <th style={{ width: '20%', textAlign: 'center', color: 'var(--brand-soft)', background: 'rgba(59,130,246,0.1)' }}>
                    Beta (Active)
                  </th>
                  <th style={{ width: '20%', textAlign: 'center' }}>Premium (Post-Beta)</th>
                </tr>
              </thead>
              <tbody>
                {TABLE_CATEGORIES.map((cat, cIdx) => (
                  <React.Fragment key={cIdx}>
                    {/* Category Header Row */}
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: '14px 18px',
                          background: 'rgba(245,241,232,0.025)',
                          borderTop: cIdx > 0 ? '1px solid var(--border-strong)' : 'none',
                          borderBottom: '1px solid var(--border)',
                          fontSize: 11.5,
                          fontWeight: 700,
                          letterSpacing: 1,
                          textTransform: 'uppercase',
                          color: 'var(--brand-soft)',
                          fontFamily: 'var(--font-code)',
                        }}
                      >
                        {cat.category}
                      </td>
                    </tr>

                    {/* Category Detail Rows */}
                    {cat.rows.map((row, rIdx) => (
                      <tr
                        key={rIdx}
                        style={{
                          background: rIdx % 2 === 0 ? 'rgba(245,241,232,0.01)' : 'transparent',
                        }}
                      >
                        <td
                          style={{
                            padding: '12px 18px',
                            borderTop: '1px solid var(--border)',
                            verticalAlign: 'middle',
                          }}
                        >
                          <div style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 500, letterSpacing: -0.2 }}>
                            {row.label}
                          </div>
                          {row.sub && (
                            <div style={{ color: 'var(--text-muted)', fontSize: 11.5, marginTop: 2 }}>
                              {row.sub}
                            </div>
                          )}
                        </td>

                        <td
                          style={{
                            padding: '12px 18px',
                            textAlign: 'center',
                            borderTop: '1px solid var(--border)',
                            verticalAlign: 'middle',
                          }}
                        >
                          <CellVal val={row.free} />
                        </td>

                        <td
                          style={{
                            padding: '12px 18px',
                            textAlign: 'center',
                            borderTop: '1px solid var(--border)',
                            background: 'rgba(59,130,246,0.04)',
                            verticalAlign: 'middle',
                          }}
                        >
                          <CellVal val={row.beta} />
                        </td>

                        <td
                          style={{
                            padding: '12px 18px',
                            textAlign: 'center',
                            borderTop: '1px solid var(--border)',
                            verticalAlign: 'middle',
                          }}
                        >
                          <CellVal val={row.premium} />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── INTERACTIVE FAQ SECTION ──────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    q: 'What is the security difference between the Free tier and Beta tier?',
    a: 'The free tier compiles single Python files to native binaries with AST symbol renaming and compression; a determined reverse engineer with decompilers could eventually reconstruct logic. The Beta tier activates the full virtualization engine, which eradicates CPython bytecode and replaces it with a polymorphic native stack machine featuring encrypted opcodes, anti-debugging traps, and active dump detection. All builds are reversible with enough effort, though.',
  },
  {
    q: 'When does the public beta period end, and how will I be notified?',
    a: 'There is currently no fixed cutoff date. We will provide at least 30 days of advance written notice via email and our official Discord community before paid tiers take effect. All early beta participants will automatically receive an exclusive grandfathered 40% lifetime discount on paid plans.',
  },
  {
    q: 'Will my compiled .pyd and .so binaries continue working after beta?',
    a: 'Yes, permanently. Every build compiles into a completely self-contained native x86_64 binary with zero phone-home telemetry or external server dependency. Your compiled extensions will continue to run perpetually, even if you never upgrade to a paid subscription.',
  },
  {
    q: 'Can I use PyK7VM for commercial proprietary software?',
    a: 'Yes. Both Free and Beta tiers grant full, 100% commercial use rights. There are zero royalties, zero per-seat runtime licensing fees, and no attribution or public disclosure is ever required.',
  },
  {
    q: "How are daily build quotas counted against my account?",
    a: 'Quotas are counted on successful compilation jobs. If a build fails due to Python syntax errors, invalid source archives, or compiler issues, it is automatically refunded and will not consume your daily quota. Quotas reset automatically every 24 hours at 00:00 UTC.',
  },
  {
    q: 'How does the 40% early-adopter grandfathered discount work?',
    a: 'Any developer registered during the public beta program is automatically tagged with an Early Adopter token. When commercial paid tiers launch post-beta, you will receive an exclusive lifetime 40% discount on all premium monthly and annual subscription plans.',
  },
  {
    q: 'Do you offer an on-premises offline compiler for air-gapped networks?',
    a: 'An on-premises offline compiler for air-gapped networks is on the product roadmap for defense contractors, financial institutions, and enterprise teams operating in sovereign environments. It is not available yet. Contact sales@pyk7vm.com to be notified when it launches.',
  },
];

function PricingFAQ() {
  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 4vw, 32px)', position: 'relative' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <Reveal>
          <div style={{ marginBottom: 36 }}>
            <h2
              style={{
                fontSize: 'clamp(28px, 4.5vw, 48px)',
                fontWeight: 500,
                letterSpacing: -1.5,
                lineHeight: 1.08,
                color: 'var(--text)',
                margin: 0,
              }}
            >
              Pricing &amp; licensing details
            </h2>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div style={{ maxWidth: 880 }}>
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── CLOSING HIGH-IMPACT HERO CTA ─────────────────────────────────────────────
function PricingClosing() {
  const [btnHover, setBtnHover] = useState(false);

  return (
    <section
      style={{
        borderTop: '1px solid var(--border)',
        padding: 'clamp(60px, 8vw, 120px) clamp(16px, 4vw, 32px)',
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, opacity: 0.25, pointerEvents: 'none' }}>
        <Aurora palette={{ a: '#1e40af', b: '#3b82f6', c: '#08080d' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 2, maxWidth: 660, margin: '0 auto' }}>
        <Reveal>
          <h2
            style={{
              fontSize: 'clamp(28px, 4.5vw, 54px)',
              fontWeight: 500,
              letterSpacing: -1.5,
              lineHeight: 1.08,
              color: 'var(--text)',
              marginBottom: 16,
            }}
          >
            Start protecting your code today.
          </h2>
        </Reveal>

        <Reveal delay={60}>
          <p
            style={{
              color: 'var(--text-dim)',
              fontSize: 'clamp(14.5px, 2vw, 16.5px)',
              lineHeight: 1.65,
              marginBottom: 32,
              maxWidth: 520,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            No credit card. No complex configuration. Your Python logic is compiled into a per-build native virtual machine with no CPython bytecode to decompile.
          </p>
        </Reveal>

        <Reveal delay={120}>
          <a
            href="register.php"
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '13px 32px',
              borderRadius: 999,
              background: 'var(--text)',
              opacity: btnHover ? 0.92 : 1,
              color: 'var(--bg)',
              fontSize: 14.5,
              fontWeight: 600,
              textDecoration: 'none',
              letterSpacing: -0.2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
              transform: btnHover ? 'translateY(-2px)' : 'none',
              transition: 'all .25s ease',
              minHeight: 46,
            }}
          >
            Create Free Account
          </a>
        </Reveal>
      </div>
    </section>
  );
}

// ─── MAIN PRICING APP ROOT ────────────────────────────────────────────────────
function PricingApp() {
  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <style>{`
        html.light .pricing-tier-card {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05) !important;
        }
        html.light .pricing-tier-card.is-featured {
          background: #ffffff !important;
          border-color: rgba(37, 99, 235, 0.45) !important;
          box-shadow: 0 8px 30px rgba(37, 99, 235, 0.12) !important;
        }
        html.light .enterprise-banner-box {
          background: #ffffff !important;
          border-color: rgba(37, 99, 235, 0.25) !important;
          box-shadow: 0 10px 40px -4px rgba(37, 99, 235, 0.08), 0 2px 12px rgba(15, 23, 42, 0.04) !important;
        }
        html.light .ent-badge-item {
          background: #f8fafc !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
        }
        html.light .table-responsive-wrapper table {
          background: #ffffff !important;
        }
        html.light .table-responsive-wrapper th {
          background: #f8fafc !important;
          color: #0f172a !important;
          border-bottom-color: rgba(15, 23, 42, 0.1) !important;
        }
        html.light .table-responsive-wrapper td {
          border-top-color: rgba(15, 23, 42, 0.06) !important;
        }
        html.light .table-responsive-wrapper td[colspan] {
          background: #eff6ff !important;
          color: #2563eb !important;
          border-top-color: rgba(37, 99, 235, 0.2) !important;
          border-bottom-color: rgba(37, 99, 235, 0.15) !important;
        }
      `}</style>
      <Nav />
      <main>
        <PricingHero />
        <PricingTiers />
        <EnterpriseBanner />
        <CompareTable />
        <PricingFAQ />
        <PricingClosing />
      </main>
      <Footer />
      <DiscordCTA />
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(PricingApp));
